import { service } from '@ember/service';

import Highcharts from 'highcharts/esm/highcharts.src.js';
import numeral from 'numeral';

import type { DChartInterface, Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { uniq } from 'webapp/utils/common';
import { sanitize } from 'webapp/utils/html';
import { formatNumber, isDurationFormat } from 'webapp/utils/number';

import Annotation from 'webapp/models-custom/annotation';
import Serviced from 'webapp/models-custom/serviced';
import { themeClassNames } from 'webapp/models-custom/theme';
import type DChart from 'webapp/d-chart/d-chart';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';
import type Series from 'webapp/d-chart/series/series';

export interface HighchartsChartWithCustomPlotlines extends Highcharts.Chart {
	customPlotLines: Highcharts.SVGElement[];
}

// DChart Renderer takes pre-prepared data from Visualization
// (usually in format of DChartSeries or DChartPrimary/ComparePoints)
// and is responsible for "translating" it into Highcharts options
// and handling changes (e.g. size, theme, data) triggered by DChart.
// Visualization can have multiple possible renderers, e.g. LineChart
// that can use it's "normal" LineOrBarChartRenderer, or a SimpleLineChartRenderer for a simplified version,
// or ForecastChartRenderer which extends LineOrBarChartRenderer with some extra options
// Read more: https://databox.atlassian.net/wiki/x/AgAb3Q

// options to merge (see BaseRenderer.mergeOptions): series are merged by index into the existing ones, so a
// fragment's series can be partial
export type OptionsFragment = Omit<Highcharts.Options, 'series'> & {
	series?: SeriesFragment[];
};

// a series to merge; custom colors use a class name as colorIndex, dbxSeries is the series model
type SeriesFragment = Highcharts.SeriesOptionsType extends infer S
	? S extends unknown
		? Partial<Omit<S, 'colorIndex'>> & { colorIndex?: number | string; dbxSeries?: Series }
		: never
	: never;

// yAxis and series as mergeTwoOptionsObj merges them: by index, when both sides have arrays (a single
// object has no length, so Highcharts.merge merges it as it is)
type MergeableOptions = Partial<Record<'yAxis' | 'series', object[]>>;

export default abstract class BaseRenderer extends Serviced {
	@service('popup') declare popupService: Service.Popup;

	dChart: DChart; // reference back to d-chart

	tooltipHideDelay = 300;

	tooltipArrowPosition = 'bottom';

	constructor(chart: DChart) {
		super();
		this.dChart = chart;
	}

	get visualization() {
		return this.dChart.visualization;
	}

	protected get isRendered(): boolean {
		return !!this.dChart.hChart;
	}

	get dataTimezone() {
		return this.visualization.dataTimezone;
	}

	get legendOptions(): Highcharts.LegendOptions {
		// can be overwritten

		const that = this;
		return {
			useHTML: true,
			symbolPadding: 0,
			symbolHeight: 0,
			symbolWidth: 0,
			itemMarginTop: 0,
			itemMarginBottom: 0,
			labelFormatter: function (this: Highcharts.Point | Highcharts.Series) {
				const unsanitizedText = that.formatSeriesTextForLegend(this),
					text = that.sanitize(unsanitizedText),
					shortenTextTo = 25,
					mustShorten = text.length - 1 > shortenTextTo,
					shortenedText = mustShorten ? `${text.substring(0, shortenTextTo)}…` : text;

				return `<span class="dbx-hc-legend-item"${mustShorten ? ` title="${text}"` : ''}><span class="legend-checkbox-on"><ui-icon class="legend-checkbox" name="SquareCheck" size="dynamic"></ui-icon></span><span class="legend-checkbox-off"><ui-icon class="legend-checkbox" name="SquareEmpty" size="dynamic"></ui-icon></span>${shortenedText}</span>`;
			},
		};
	}

	get baseOptions(): Highcharts.Options {
		// not dependent on chart type, the same for all
		const options = {
			chart: {
				styledMode: true,
				animation: this.dChart.isAnimated,
				spacing: [0, 0, 0, 0], // cancel default spacing
			},
			plotOptions: {
				series: {
					animation: this.dChart.isAnimated,
				},
			},
			title: {
				text: undefined,
			},
			credits: {
				enabled: false,
			},
			legend: this.legendOptions,
		};
		return options;
	}

	// likely is overwritten in specific charts
	// must return Highcharts.Options-compatible format for options that involve data
	get dataOptions(): Highcharts.Options {
		const options = {};
		return options;
	}

	// likely is overwritten in specific charts
	// must return Highcharts.Options-compatible format for options that are changed bcos of the chart sizeModifier (fontsize)
	get sizeOptions(): Highcharts.Options {
		const options = {};
		return options;
	}

	// likely is overwritten in specific charts
	// must return Highcharts.Options-compatible format for options that involve theme and color changes
	get themeOptions(): Highcharts.Options {
		const options = {};
		return options;
	}

	get tooltipOptions(): Highcharts.Options {
		const that = this;

		const options: Highcharts.TooltipOptions = {
			useHTML: true,
			distance: this.tooltipDistance,
			padding: 0,
			borderRadius: 5,
			outside: true,
			borderColor: 'black',
			formatter: function (
				this: Highcharts.Point & { point?: Highcharts.Point },
				tooltip: Highcharts.Tooltip,
			) {
				// if the popup was inside, props would be inherited from parent block/board
				// because the popup is outside, we need to add them manually
				const hcPoint = (this.point || this.points![0]) as DChartInterface.ChartHoverPoint,
					point = hcPoint?.dbxPoint;

				if (!point) {
					return '';
				}

				const { html, typeClass } = that.getTooltipForPoint(point);
				const tooltipBaseClass = 'highcharts-dbx-tooltip',
					tooltipTypeClass = `${tooltipBaseClass}--${typeClass}`;

				if (!html) {
					return '';
				}

				const fontSize = Math.max(
						7,
						Math.min(12, that.dChart.sizeModifier / that.fontSizeCoefficient),
					), // 7-12px, ignoring user font size setting
					themeId = that.dChart.themeData.themeId,
					bgTheme = themeId === 5 ? 'light' : that.dChart.themeData.bgTheme; // the black color theme always must be on white tooltip otherwise names are unreadable

				// colors in the tooltip html get stripped out for some unknown reason, so we set them on container manually
				that.updateCustomColorsInTooltip(tooltip);

				return (
					`<div class="colorTheme-${themeId} bgTheme-${bgTheme} ${themeClassNames(bgTheme)}" style="font-size:${fontSize}px">` +
					`<div class="${tooltipBaseClass} ${tooltipTypeClass} arrow--${that.tooltipArrowPosition}">${html}</div>` +
					'</div>'
				);
			},
			stickOnContact: false, // "true" has problem with "outside: true" - issue https://github.com/highcharts/highcharts/issues/14434
			shape: 'rect',
			hideDelay: this.tooltipHideDelay,
		};

		return { tooltip: options };
	}

	get fullOptions(): Highcharts.Options {
		let options = this.baseOptions;

		const dataOptions = this.dataOptions;
		const tooltipOptions = this.tooltipOptions;
		const sizeOptions = this.sizeOptions;
		const themeOptions = this.themeOptions;

		options = this.mergeOptions(
			options,
			dataOptions,
			tooltipOptions,
			sizeOptions,
			themeOptions,
		);

		return options;
	}

	get tooltipDistance(): number {
		return 14;
	}

	get defaultLegendOptions(): Highcharts.LegendOptions {
		const m = this.dChart.sizeModifier / this.fontSizeCoefficient;
		return {
			maxHeight: Math.max(50, this.dChart.maxHeight / 3),
			alignColumns: false,
			margin: Math.max(m, 8),
			padding: m / 2, // to avoid cutoff labels
			itemDistance: 1.5 * m,
			navigation: {
				// enabled: false, // if we disable navigation, maxHeight setting is ignored
				arrowSize: 0.8 * m,
			},
		};
	}

	get fontSizeCoefficient(): number {
		const fontSize = this.visualization.blockSetup.block.fontSize;
		return this.visualization.mmBlocksService.getModifierFromFontSize(fontSize);
	}

	getTooltipForPoint(point: DChartPrimaryPoint) {
		// overwritten in line-or-bar-chart bcos of compares
		// and funnel/pipeline bcos of skipping empty tooltips
		return {
			html: point.tooltipHtml,
			typeClass: point.tooltipTypeClass,
		};
	}

	// likely is overwritten in specific charts
	// called when data has changed on d-chart
	updateData(): void {
		const options = this.dataOptions;
		this.dChart.updateHChart(options);
	}

	// likely is overwritten in specific charts
	// called when size has changed on d-chart
	// with param if it's modifier changed
	// most charts only need to handle changes only to modifier (exceptions: gauge)
	// if we use dChart height/width in the sizing calculations (e.g. defaultLegendOptions), the update must be always called
	updateSize(hasModifierChanged: boolean): void {
		if (hasModifierChanged) {
			const options = this.sizeOptions;
			this.dChart.updateHChart(options);
		}
	}

	// likely is overwritten in specific charts
	// called when theme has changed on d-chart
	updateTheme(): void {
		const options = this.themeOptions;
		this.dChart.updateHChart(options);
	}

	updateDefs(): void {
		if (this.isRendered) {
			// for defs, must set them explicitly in renderer, because highcharts ignores defs sent through hChart.update
			const hChartRenderer = this.dChart.hChart!.renderer,
				defsExistingIds: string[] = [];

			Array.from(hChartRenderer.defs?.element?.children || []).forEach((child) => {
				const id = child.id;
				if (id) {
					// remove all "custom-color" defs bcos we reuse indexes
					// the used ones will be added again anyways
					if (id.includes('custom-color')) {
						child.remove();
					} else {
						defsExistingIds.push(id);
					}
				}
			});

			this.getDefs().forEach((def) => {
				if (!defsExistingIds.includes(def.id)) {
					hChartRenderer.definition(def);
				}
			});
		}
	}

	// likely is overwritten in specific charts that have special defs
	protected getDefs(): DChartInterface.Def[] {
		return [];
	}

	hideTooltip(delay = 0): void {
		this.dChart.hChart?.tooltip?.hide(delay);
	}

	private updateCustomColorsInTooltip(tooltip: Highcharts.Tooltip, isRetry = false) {
		const container = tooltip.container;
		if (container) {
			const colorTokenStyles = this.dChart.customColorClassMap;
			Object.entries(colorTokenStyles).map((entry) => {
				const [hex, customClass] = entry;
				container.style.setProperty(`--${customClass}`, hex);
			});
		} else if (!isRetry) {
			setTimeout(() => {
				this.updateCustomColorsInTooltip(tooltip, true);
			}, 1);
		}
	}

	// a legend item: a series here, a point on charts that list points (pie)
	formatSeriesTextForLegend(series: Highcharts.Series | Highcharts.Point): string {
		const primaryItems = (series as Highcharts.Series).chart.series.filter((s) => {
			return (s.userOptions as DChartInterface.SeriesOptions).dbxSeries?.isMain;
		});
		const seriesModel = (
			(series as Highcharts.Series).userOptions as DChartInterface.SeriesOptions
		).dbxSeries;

		let title = series.name;

		if (seriesModel) {
			const showShortLegend = primaryItems.length <= 1 && !seriesModel.isGoal;

			title = showShortLegend
				? seriesModel?.titleForLegendShort
				: seriesModel?.titleForLegend;
		}

		return title;
	}

	// eslint-disable-next-line ember/classic-decorator-hooks
	destroy(): void {
		// ?
	}

	protected sanitize(text: string) {
		return sanitize(text);
	}

	protected valueAxisFormatter(
		point: Highcharts.AxisLabelsFormatterContextObject,
		makeFirstSpaceUnbreakable = true,
	) {
		const value = (point.value as number) || 0; // a value axis

		// see if we should use numeral lib auto formatting or our logic (formatNumber)
		// if we format ourselves, use first point's scale and format

		let useLibAutoFormatting = true,
			yAxisScale: number | null = null,
			yAxisFormat = '0';

		const series = point.axis.series.find((s) => {
			return (s.userOptions as DChartInterface.SeriesOptions | undefined)?.dbxSeries?.isMain;
		});
		if (series) {
			const point = (
					(series.userOptions as Highcharts.SeriesLineOptions)
						.data![0] as DChartInterface.ChartDataPoint
				)?.dbxPoint,
				format = point?.format;
			if (format) {
				yAxisFormat = format;
				const isPercentage = format.includes('%') || format.includes('◊'),
					isDuration = isDurationFormat(format),
					isRelativeTime = format === 'R';
				useLibAutoFormatting = !isPercentage && !isDuration && !isRelativeTime;
			}
			if (typeof point?.scale === 'number') {
				yAxisScale = point.scale;
			}
		}

		if (useLibAutoFormatting) {
			// if using auto formatting, set the correct amount of decimals for auto format
			// just enough decimals so that none of the axis labels repeat
			// also check one step below and above so we avoid rounding errors for top/bottom labels
			const allAxisLabels: number[] = Array.from(point.axis.tickPositions!); // set by the time labels are formatted
			const step = Math.abs(allAxisLabels[1]! - allAxisLabels[0]!);
			if (step) {
				allAxisLabels.unshift(allAxisLabels[0]! - step);
				allAxisLabels.push(allAxisLabels[allAxisLabels.length - 1]! + step);
			}

			let format = '0a',
				allAxisLabelsFormatted = allAxisLabels.map((p) => numeral(p).format(format));
			if (allAxisLabelsFormatted.length !== uniq(allAxisLabelsFormatted).length) {
				format = '0.[0]a';
				allAxisLabelsFormatted = allAxisLabels.map((p) => numeral(p).format(format));
				if (allAxisLabelsFormatted.length !== uniq(allAxisLabelsFormatted).length) {
					format = '0.[00]a';
					allAxisLabelsFormatted = allAxisLabels.map((p) => numeral(p).format(format));
				}
			}
			return numeral(value).format(format);
		} else {
			const opts = {
				scale: yAxisScale,
				allowExtraDigits: Math.abs(value) < 100,
				makeFirstSpaceUnbreakable,
			};
			return formatNumber(value, yAxisFormat, opts);
		}
	}

	protected sendEventForBlockContentClick(detail: { dsId: number }): void {
		// fake an event for block::content/handleBlockContentEvent
		const fakeEvent = new CustomEvent('click', {
			detail,
			bubbles: true,
		});
		this.dChart.node.dispatchEvent(fakeEvent);
	}

	protected centerLegendItems(legend: Highcharts.Legend): void {
		// group.div (the HTML legend's container) and legendWidth are Highcharts internals
		const items =
				(
					legend.group as Highcharts.SVGElement & { div?: HTMLElement }
				)?.div?.querySelectorAll<HTMLElement>('.highcharts-legend-item') || [],
			legendW = (legend as Highcharts.Legend & { legendWidth: number }).legendWidth,
			distance = (this.dChart.sizeModifier / this.fontSizeCoefficient) * 1.5,
			rows: HTMLElement[][] = [];

		// first, sort items by rows
		let prevTop = 0,
			row = 0;
		items.forEach((item: HTMLElement, i: number) => {
			const top = parseFloat(item.style.top);

			if (!i) {
				prevTop = top;
			}

			if (prevTop !== top) {
				prevTop = top;
				row++;
			}

			if (!rows[row]) {
				rows[row] = [];
			}

			rows[row]!.push(item);
		});

		rows.forEach((items: HTMLElement[]) => {
			let rowStart = 0,
				rowEnd = 0;

			const widths: number[] = [];

			items.forEach((item: HTMLElement, i: number) => {
				if (i) {
					rowEnd += distance;
				} else {
					rowStart = parseFloat(item.style.left);
				}

				const innerEl = item.querySelector('span') as HTMLElement; // this el actually has width

				if (innerEl) {
					const itemWidth =
						parseFloat(innerEl.style.left) + innerEl.getBoundingClientRect().width;
					widths[i] = itemWidth;
					rowEnd += itemWidth;
				}
			});

			const rowWidth = rowEnd - rowStart,
				left = (legendW - rowWidth) / 2;

			let currentLeft = left;
			items.forEach((item: HTMLElement, i: number) => {
				const itemWidth = widths[i]!,
					itemLeft = parseFloat(item.style.left);

				if (Math.round(currentLeft) !== Math.round(itemLeft)) {
					if (this.dChart.isAnimated) {
						try {
							// old browsers don't support native animations
							if (!isNaN(currentLeft) && typeof currentLeft === 'number') {
								item.animate([{ left: `${currentLeft}px` }], {
									duration: 200,
									fill: 'forwards',
								});
							}
						} catch (_e) {
							// skip
						}
					} else {
						item.style.left = `${currentLeft}px`;
					}
				}

				currentLeft += itemWidth + distance;
			});
		});
	}

	protected mergeOptions(
		options1: OptionsFragment,
		...restOptions: OptionsFragment[]
	): Highcharts.Options;
	// also merges other options objects, like an axis' (with an explicit T)
	protected mergeOptions<T extends object>(options1: T, ...restOptions: Partial<T>[]): T;
	protected mergeOptions(options1: object, ...restOptions: object[]): object {
		const that = this;
		// for options that take array like yAxis we need special merging rules
		// since Highcharts.merge just overwrites them
		const mergeTwoOptionsObjsFn = function (
			options1: MergeableOptions,
			options2: MergeableOptions,
		): MergeableOptions {
			if (options1.yAxis?.length && options2.yAxis?.length) {
				// merge the less yAxis count into more yAxis count option
				that.mergeTwoOptionsObj(options1, options2, 'yAxis');
			}

			if (options1.series?.length && options2.series?.length) {
				// merge the less series count into more series count option
				that.mergeTwoOptionsObj(options1, options2, 'series');
			}

			return Highcharts.merge({}, options1, options2);
		};
		return restOptions.reduce(mergeTwoOptionsObjsFn, options1);
	}

	protected getAnnotationsByAxisTsForSeries(series: Series | null) {
		const annotationsByAxisTs: Record<string, Annotation[]> = {};

		(series?.granulationLabels || []).forEach((granulationData) => {
			const annotations = this.getAnnotationsForTsInterval(
				granulationData.ts,
				granulationData.endTs,
			);
			annotationsByAxisTs[granulationData.ts] = [...annotations];
		});

		return annotationsByAxisTs;
	}

	protected getAnnotationsForTsInterval(startTs: number, endTs: number): Annotation[] {
		const timezone = this.visualization.dataTimezone,
			annotationsData = this.visualization.sample?.sampledata?.annotations || [];

		const annotations = annotationsData
			.filter((annotationData) => {
				return annotationData.timestamp >= startTs && annotationData.timestamp < endTs;
			})
			.map((annotationData) => {
				return Annotation.createFromModel(annotationData, timezone);
			});

		return annotations;
	}

	protected onPointClick(
		dbxPoint: DChartPrimaryPoint,
		event: Event, // a DOM event, or Highcharts' click event object
		target?: EventTarget,
	): void {
		if (!dbxPoint?.onClick) {
			return;
		}

		const setTooltipEnabled = (enabled: boolean) => {
			this.dChart.updateHChart({ tooltip: { enabled } });
		};

		const popup = dbxPoint.onClick?.({ ...event, target: target || null } as PointerEvent);

		if (!popup) {
			return;
		}

		popup.on(PopupEvent.OPENED, this, () => {
			setTooltipEnabled(false);
		});

		popup.on(PopupEvent.CLOSED, this, () => {
			setTooltipEnabled(true);
		});
	}

	private mergeTwoOptionsObj(
		options1: MergeableOptions,
		options2: MergeableOptions,
		property: keyof MergeableOptions,
	): void {
		const mergeInto =
				options1[property]!.length >= options2[property]!.length ? options1 : options2,
			mergeFrom = mergeInto === options1 ? options2 : options1;
		mergeInto[property] = mergeInto[property]!.map((prop, i: number) => {
			return Highcharts.merge({}, prop, mergeFrom[property]![i] || mergeFrom[property]![0]);
		});
		delete mergeFrom[property];
	}
}
